// Product Specification Section component - extracted from ClassificationStep
// Renders dynamic category attributes based on selected category

import React from "react";
import { SelectField, FormField } from "src/shared/forms/components";
import type { AccountCategoryAttribute } from "../../../../category/types/AccountCategory";

export interface ProductSpecificationSectionProps {
  attributesLoading: boolean;
  attributesError: string | null;
  sortedAttributes: AccountCategoryAttribute[];
  categoryAttributeValues: Record<string, any>;
  onAttributeChange: (attributeName: string, value: any) => void;
}

// Render dynamic attribute field based on attribute type
const renderAttributeField = (
  attr: AccountCategoryAttribute,
  value: any,
  onAttributeChange: (attributeName: string, value: any) => void
) => {
  switch (attr.inputType) {
    case "DROP_DOWN":
    case "COMBO_BOX":
      return (
        <SelectField
          value={value as string}
          onChange={(v) => onAttributeChange(attr.originalAttributeName, v)}
          placeholder={`Select ${attr.displayAttributeName}...`}
          searchable={true}
          options={[
            {
              value: "",
              label: `Select ${attr.displayAttributeName}...`,
              disabled: true,
            },
            ...(attr.attributeValues || []).map((av) => ({
              value: av.originalValueName,
              label: av.displayValueName,
            })),
          ]}
          className="tw-w-full"
        />
      );
    case "NUMBER_FIELD":
      return (
        <input
          type="number"
          value={value as string}
          onChange={(e) => onAttributeChange(attr.originalAttributeName, e.target.value)}
          placeholder={`Enter ${attr.displayAttributeName}...`}
          className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
        />
      );
    case "TEXT_FIELD":
    default:
      return (
        <input
          type="text"
          value={value as string}
          onChange={(e) => onAttributeChange(attr.originalAttributeName, e.target.value)}
          placeholder={`Enter ${attr.displayAttributeName}...`}
          className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
        />
      );
  }
};

const ProductSpecificationSection: React.FC<ProductSpecificationSectionProps> = ({
  attributesLoading,
  attributesError,
  sortedAttributes,
  categoryAttributeValues,
  onAttributeChange,
}) => {
  return (
    <div className="tw-bg-surface-light dark:tw-bg-surface-dark tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-[#233648] tw-p-4 md:tw-p-6 tw-shadow-sm tw-mt-6">
      <div className="tw-mb-6 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-pb-4">
        <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
          Product Specification
        </h3>
        <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
          Product-specific attributes that depend on the selected category.
        </p>
      </div>

      {/* Loading state */}
      {attributesLoading && (
        <div className="tw-flex tw-items-center tw-justify-center tw-py-8">
          <div className="tw-animate-spin tw-h-6 tw-w-6 tw-border-2 tw-border-primary tw-border-t-transparent tw-rounded-full"></div>
          <span className="tw-ml-3 tw-text-gray-500">
            Loading category attributes...
          </span>
        </div>
      )}

      {/* Error state */}
      {attributesError && !attributesLoading && (
        <div className="tw-text-sm tw-text-red-600 tw-py-4">
          Error loading category attributes: {attributesError}
        </div>
      )}

      {/* All Attributes (required + optional) */}
      {!attributesLoading && !attributesError && sortedAttributes.length > 0 && (
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
          {sortedAttributes.map((attr) => (
            <FormField
              key={attr.attributeId}
              name={attr.originalAttributeName}
              label={attr.displayAttributeName}
              required={attr.isMandatory}
            >
              {renderAttributeField(
                attr,
                categoryAttributeValues[attr.originalAttributeName] || "",
                onAttributeChange
              )}
            </FormField>
          ))}
        </div>
      )}

      {/* No attributes found */}
      {!attributesLoading && !attributesError && sortedAttributes.length === 0 && (
        <div className="tw-text-sm tw-text-gray-500 tw-py-4">
          This category does not require additional attributes.
        </div>
      )}
    </div>
  );
};

export default ProductSpecificationSection;
